import { TeamOutlined } from "@ant-design/icons";
import { InputNumber, Select } from "antd";
import React from "react";
import { BsBoxSeam } from "react-icons/bs";
import { LuTruck } from "react-icons/lu";
import { TbWallet } from "react-icons/tb";

// import { ForecastVariations } from "@/components/forecast/ForecastVariations";
import { CostSection } from "@/components/forecasting/costs/CostSection";
import {
  CostGroup,
  CostOutline,
  CostTable,
  CostValue
} from "@/components/forecasting/costs/CostTable";
import {
  formatAmount,
  getForecastTotal,
  sumAmounts
} from "@/components/forecasting/forecastTotals";
import { BUSINESS_UNIT_TO_CODE_MAP } from "@/constants/region";
import { useUser } from "@/contexts/UserContext";
import { useGetWorkers } from "@/hooks/chime";
import { useGetAllInternalPlantItems } from "@/hooks/plantItems";
// import { useGetProjectVariations } from "@/hooks/projectVariation";
import {
  useGetSubcontractors,
  useGetSubcontractorTrades
} from "@/hooks/useGetSubcontractors";
import {
  ILabourCostEntry,
  IPlantCostEntry,
  IProjectCostInput,
  ISubcontractorCostEntry,
  ITaskAllocation
} from "@/pages/ForecastingPage";
import {
  IForecastingCentralRepoRes,
  IForecastingRes
} from "@/types/forecasting";
import {
  DEFAULT_HOURS_PER_DAY,
  getLabourAmount,
  getLabourDayRate
} from "@/utils/labour";
import { getPlantAmount, getPlantCategories } from "@/utils/plant";
import {
  filterOptionForLabelAndValueSearch,
  sortByLabel
} from "@/utils/selectUtils";

export const AddCostDurationStep = ({
  projectCosts,
  setProjectCosts,
  variations,
  setVariations,
  expandedSections,
  toggleSection,
  projectCentralRepoData,
  taskAllocations,
  monthForecastData,
  isPrefilled,
  opportunityNo
}: {
  projectCosts: IProjectCostInput;
  setProjectCosts: React.Dispatch<React.SetStateAction<IProjectCostInput>>;
  variations: Array<{
    id: string;
    variation: string;
    utilizedAmount: number;
    availableAmount: number;
    totalAmount: number;
  }>;
  setVariations: React.Dispatch<
    React.SetStateAction<
      Array<{
        id: string;
        variation: string;
        utilizedAmount: number;
        availableAmount: number;
        totalAmount: number;
      }>
    >
  >;
  expandedSections: {
    labour: boolean;
    plant: boolean;
    materials: boolean;
    subcontractors: boolean;
  };
  toggleSection: (
    section: "labour" | "plant" | "materials" | "subcontractors"
  ) => void;
  projectCentralRepoData: IForecastingCentralRepoRes;
  taskAllocations: ITaskAllocation[];
  monthForecastData?: IForecastingRes;
  /** Costs are copied from a saved forecast, so the material amount must not be reset to the estimate. */
  isPrefilled?: boolean;
  opportunityNo?: string;
}) => {
  const { data: subcontractorTrades = [], isLoading: tradesLoading } =
    useGetSubcontractorTrades();
  const { data: subcontractors = [], isLoading: subcontractorsLoading } =
    useGetSubcontractors();

  const subcontractorsByTrade = React.useMemo(() => {
    const groups: Record<string, typeof subcontractors> = {};
    subcontractors.forEach((subcontractor) => {
      (groups[subcontractor.tradeCode] ??= []).push(subcontractor);
    });
    return groups;
  }, [subcontractors]);

  const [addVariation, setAddVariation] = React.useState(false);

  const user = useUser();

  // Compute estimated costs from costBreakdown for each individually selected task.
  // Builds a taskId → costBreakdown map first (O(tasks)), then sums per allocation (O(allocations)).
  React.useEffect(() => {
    type Breakdown = {
      labour_rate: number;
      plant_rate: number;
      material_rate: number;
      subcontractor_rate: number;
    };
    const breakdownMap = new Map<string, Breakdown | null | undefined>();
    for (const plot of projectCentralRepoData.groundworks?.plots ?? []) {
      for (const stage of plot.stages ?? []) {
        for (const task of stage.tasks ?? []) {
          breakdownMap.set(task.taskId, task.costBreakdown);
        }
      }
    }
    for (const run of projectCentralRepoData.roadsAndSewers?.runs ?? []) {
      for (const stage of run.stages ?? []) {
        for (const task of stage.tasks ?? []) {
          breakdownMap.set(task.taskId, task.costBreakdown);
        }
      }
    }

    let totalLabour = 0;
    let totalPlant = 0;
    let totalMaterial = 0;
    let totalSubcontractor = 0;
    const seenTaskIds = new Set<string>();

    taskAllocations?.forEach((alloc) => {
      if (seenTaskIds.has(alloc.taskId)) return;
      seenTaskIds.add(alloc.taskId);
      const bd = breakdownMap.get(alloc.taskId);
      totalLabour += bd?.labour_rate ?? 0;
      totalPlant += bd?.plant_rate ?? 0;
      totalMaterial += bd?.material_rate ?? 0;
      totalSubcontractor += bd?.subcontractor_rate ?? 0;
    });

    setProjectCosts((prev) => ({
      ...prev,
      estimatedLabourCost: totalLabour,
      estimatedPlantCost: totalPlant,
      estimatedMaterialCost: totalMaterial,
      estimatedSubcontractorCost: totalSubcontractor,
      // Without a saved forecast to copy from, default the material amount to
      // the estimated total. Copied costs are applied by `useForecastCostPrefill`.
      ...(!isPrefilled && {
        materialCosts: [{ amount: parseFloat(totalMaterial.toFixed(2)) }]
      })
    }));
  }, [taskAllocations, projectCentralRepoData, isPrefilled]);

  // const { isLoading: apiVariationsLoading, data: apiVariations } =
  //   useGetProjectVariations(projectCentralRepoData?.projectId, opportunityNo);
  const { isLoading: internalPlantItemsLoading, data: internalPlantItemsData } =
    useGetAllInternalPlantItems();
  const businessUnitId = user?.regionCode
    ? BUSINESS_UNIT_TO_CODE_MAP[user.regionCode]
    : undefined;
  const { isLoading: workersLoading, data: workersData = [] } =
    useGetWorkers(businessUnitId);
  const workersDataRegioned = workersData;

  const plantCategories = React.useMemo(
    () => getPlantCategories(internalPlantItemsData?.value ?? []),
    [internalPlantItemsData]
  );

  const labourTradeGroups = React.useMemo(() => {
    const groups: Record<string, typeof workersDataRegioned> = {};
    workersDataRegioned.forEach((person) => {
      const trade = person.trade?.trim() || "Uncategorized";
      if (!groups[trade]) groups[trade] = [];
      groups[trade].push(person);
    });
    return groups;
  }, [workersDataRegioned]);

  const labourTradeOptions = React.useMemo(() => {
    return Object.keys(labourTradeGroups).sort();
  }, [labourTradeGroups]);

  // Calculate totals
  const labourTotal = sumAmounts(projectCosts.labourCosts);
  const plantTotal = sumAmounts(projectCosts.plantCosts);
  const materialTotal = sumAmounts(projectCosts.materialCosts);
  const subcontractorTotal = sumAmounts(projectCosts.subcontractorCosts);
  const grandTotal = getForecastTotal(projectCosts, variations);

  // Helper functions for managing costs
  const addLabourCost = () => {
    setProjectCosts((prev) => ({
      ...prev,
      labourCosts: [
        ...prev.labourCosts,
        {
          id: `_new_${Date.now()}`,
          name: "",
          labourTrade: "",
          ratePerHour: 0,
          hoursPerDay: DEFAULT_HOURS_PER_DAY,
          noOfDays: 1,
          amount: 0
        }
      ]
    }));
  };

  const updateLabourCost = (id: string, updates: Partial<ILabourCostEntry>) => {
    setProjectCosts((prev) => ({
      ...prev,
      labourCosts: prev.labourCosts.map((entry) => {
        if (entry.id === id) {
          const updated = { ...entry, ...updates };
          return { ...updated, amount: getLabourAmount(updated) };
        }
        return entry;
      })
    }));
  };

  const removeLabourCost = (id: string) => {
    setProjectCosts((prev) => ({
      ...prev,
      labourCosts: prev.labourCosts.filter((entry) => entry.id !== id)
    }));
  };

  const addPlantCost = () => {
    setProjectCosts((prev) => ({
      ...prev,
      plantCosts: [
        ...prev.plantCosts,
        {
          id: `_new_${Date.now()}`,
          name: "",
          unitOfMeasure: "",
          unitCost: 0,
          quantity: 1,
          noOfDays: 1,
          amount: 0
        }
      ]
    }));
  };

  const updatePlantCost = (id: string, updates: Partial<IPlantCostEntry>) => {
    setProjectCosts((prev) => ({
      ...prev,
      plantCosts: prev.plantCosts.map((entry) => {
        if (entry.id === id) {
          const updated = { ...entry, ...updates };
          return { ...updated, amount: getPlantAmount(updated) };
        }
        return entry;
      })
    }));
  };

  const removePlantCost = (id: string) => {
    setProjectCosts((prev) => ({
      ...prev,
      plantCosts: prev.plantCosts.filter((entry) => entry.id !== id)
    }));
  };

  const updateMaterialAmount = (value: number) => {
    const amount = parseFloat(value.toFixed(2));
    setProjectCosts((prev) => ({
      ...prev,
      materialCosts:
        prev.materialCosts.length === 0
          ? [{ amount }]
          : prev.materialCosts.map((entry, index) =>
              index === 0 ? { ...entry, amount } : entry
            )
    }));
  };

  const addSubcontractorCost = () => {
    setProjectCosts((prev) => ({
      ...prev,
      subcontractorCosts: [
        ...prev.subcontractorCosts,
        {
          name: "",
          tradeCode: "",
          amount: 0
        }
      ]
    }));
  };

  const updateSubcontractorCost = (
    index: number,
    updates: Partial<ISubcontractorCostEntry>
  ) => {
    setProjectCosts((prev) => ({
      ...prev,
      subcontractorCosts: prev.subcontractorCosts.map((entry, i) =>
        i === index ? { ...entry, ...updates } : entry
      )
    }));
  };

  const removeSubcontractorCost = (index: number) => {
    setProjectCosts((prev) => ({
      ...prev,
      subcontractorCosts: prev.subcontractorCosts.filter((_, i) => i !== index)
    }));
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Budget Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
              <TbWallet className="text-blue-500 text-xl" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {grandTotal.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Total Budget</div>
        </div>

        {/* Labour Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
              <TeamOutlined className="text-blue-500 text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {labourTotal.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Labour</div>
        </div>

        {/* Plant Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
              <LuTruck className="text-[#F59E0B] text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {plantTotal.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Plant</div>
        </div>

        {/* Materials Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
              <BsBoxSeam className="text-[#22C55E] text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {(materialTotal + subcontractorTotal).toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">
            Materials & Subcontractors
          </div>
        </div>
      </div>

      {/* Add Variation Section */}
      {/* <div className="bg-white border border-solid border-[#E5E7EB] rounded-xl mb-5 overflow-hidden">
        <div
          className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer"
          onClick={() => setAddVariation(!addVariation)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md border-2 border-blue-400 bg-[#EFF6FF] flex items-center justify-center">
              <LuCirclePlus className="text-[#167EF9] text-[15px]" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Variations</div>
              <div className="text-xs text-gray-500">
                Request budget adjustment if needed
              </div>
            </div>
          </div>
          <Button
            type="text"
            icon={
              addVariation ? (
                <MinusCircleOutlined className="text-gray-400" />
              ) : (
                <PlusCircleOutlined className="text-gray-400" />
              )
            }
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAddVariation(!addVariation);
            }}
          />
        </div>

        {addVariation && (
          <ForecastVariations
            project={projectCentralRepoData?.projectId}
            variations={variations}
            setVariations={setVariations}
            apiVariationsLoading={apiVariationsLoading}
            apiVariations={apiVariations}
            monthForecastData={monthForecastData}
          />
        )}
      </div> */}

      <CostSection
        title="Labour Costs"
        icon={
          <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
            <TeamOutlined className="text-blue-500 text-lg" />
          </div>
        }
        headerClassName="bg-blue-50"
        itemCount={projectCosts.labourCosts.length}
        total={labourTotal}
        estimated={projectCosts.estimatedLabourCost}
        expanded={expandedSections.labour}
        onToggle={() => toggleSection("labour")}
        addButton={{
          label: "Add Labour",
          onClick: addLabourCost,
          loading: workersLoading
        }}
      >
        <CostTable
          rowPadding="py-5"
          columns={[
            {
              key: "labour",
              header: "Category / Labour",
              className: "flex-1 min-w-[260px]"
            },
            {
              key: "rates",
              header: (
                // Padded to line up with the outlined group in each row.
                <div className="px-3">
                  <CostGroup>
                    <span>Rate Per Hour</span>
                    <span>Hours Per Day</span>
                    <span>Day Rate</span>
                    <span>No. of Days</span>
                  </CostGroup>
                </div>
              ),
              className: "w-[480px]"
            }
          ]}
          rows={projectCosts.labourCosts.map((entry) => {
            const filteredWorkers = entry.labourTrade
              ? labourTradeGroups[entry.labourTrade] || []
              : workersDataRegioned;

            return {
              key: entry.id,
              amount: entry.amount,
              removeLabel: `Remove ${entry.name || "labour entry"}`,
              onRemove: () => removeLabourCost(entry.id),
              cells: {
                labour: (
                  <CostOutline>
                    <div className="flex flex-col gap-3">
                      <Select
                        showSearch
                        aria-label="Category"
                        className="w-full"
                        placeholder="Select category"
                        value={entry.labourTrade || undefined}
                        filterOption={filterOptionForLabelAndValueSearch}
                        onChange={(value) => {
                          if (!entry.id.startsWith("_new_")) {
                            updateLabourCost(entry.id, {
                              id: `_new_${Date.now()}`,
                              name: "",
                              labourTrade: value,
                              ratePerHour: 0,
                              hoursPerDay: DEFAULT_HOURS_PER_DAY,
                              noOfDays: 1
                            });
                          } else {
                            updateLabourCost(entry.id, { labourTrade: value });
                          }
                        }}
                        options={sortByLabel(
                          labourTradeOptions.map((trade) => ({
                            key: trade,
                            value: trade,
                            label: trade
                          }))
                        )}
                      />
                      <Select
                        showSearch
                        aria-label="Labour"
                        className="w-full"
                        placeholder={
                          entry.labourTrade
                            ? "Select labour"
                            : "Select category first"
                        }
                        disabled={!entry.labourTrade}
                        value={
                          entry.id.startsWith("_new_") ? undefined : entry.id
                        }
                        filterOption={filterOptionForLabelAndValueSearch}
                        onChange={(value) => {
                          const person = workersDataRegioned.find(
                            (p) => p.id === value
                          );
                          updateLabourCost(entry.id, {
                            id: value,
                            name: person?.fullName || "",
                            ratePerHour: person?.hourlyRate || 0,
                            noOfDays: 1
                          });
                        }}
                        options={sortByLabel(
                          filteredWorkers.map((person) => ({
                            key: person.id,
                            value: person.id,
                            label: person.fullName,
                            disabled: projectCosts.labourCosts.some(
                              (l) => l.id === person.id && l.id !== entry.id
                            )
                          }))
                        )}
                      />
                    </div>
                  </CostOutline>
                ),
                rates: (
                  <CostOutline>
                    <CostGroup>
                      <CostValue>£{formatAmount(entry.ratePerHour)}</CostValue>
                      <InputNumber
                        aria-label="Hours per day"
                        className="w-full"
                        value={entry.hoursPerDay}
                        min={0.5}
                        max={24}
                        step={0.5}
                        onChange={(value) =>
                          updateLabourCost(entry.id, {
                            hoursPerDay: value || DEFAULT_HOURS_PER_DAY
                          })
                        }
                      />
                      <CostValue>
                        £{formatAmount(getLabourDayRate(entry))}
                      </CostValue>
                      <InputNumber
                        aria-label="No. of days"
                        className="w-full"
                        value={entry.noOfDays}
                        min={1}
                        onChange={(value) =>
                          updateLabourCost(entry.id, { noOfDays: value || 1 })
                        }
                      />
                    </CostGroup>
                  </CostOutline>
                )
              }
            };
          })}
        />
      </CostSection>

      <CostSection
        title="Plant & Equipment"
        icon={
          <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
            <LuTruck className="text-[#F59E0B] text-lg" />
          </div>
        }
        headerClassName="bg-orange-50"
        itemCount={projectCosts.plantCosts.length}
        total={plantTotal}
        estimated={projectCosts.estimatedPlantCost}
        expanded={expandedSections.plant}
        onToggle={() => toggleSection("plant")}
        addButton={{
          label: "Add Plant",
          onClick: addPlantCost,
          loading: internalPlantItemsLoading
        }}
      >
        <CostTable
          columns={[
            {
              key: "category",
              header: "Category",
              className: "flex-1 min-w-[260px]"
            },
            {
              key: "unitOfMeasure",
              header: "Unit of Measure",
              className: "w-[120px]"
            },
            { key: "unitCost", header: "Unit Cost", className: "w-[110px]" },
            { key: "quantity", header: "Quantity", className: "w-[100px]" },
            { key: "noOfDays", header: "No. of Days", className: "w-[100px]" }
          ]}
          rows={projectCosts.plantCosts.map((entry) => ({
            key: entry.id,
            amount: entry.amount,
            removeLabel: `Remove ${entry.name || "plant entry"}`,
            onRemove: () => removePlantCost(entry.id),
            cells: {
              category: (
                <Select
                  showSearch
                  aria-label="Category"
                  className="w-full"
                  placeholder="Select category"
                  value={entry.name || undefined}
                  filterOption={filterOptionForLabelAndValueSearch}
                  onChange={(value) => {
                    const category = plantCategories.get(value);
                    updatePlantCost(entry.id, {
                      id: value,
                      name: value,
                      unitOfMeasure: category?.unitOfMeasure ?? "",
                      unitCost: category?.unitCost ?? 0
                    });
                  }}
                  options={sortByLabel(
                    [...plantCategories.keys()].map((name) => ({
                      key: name,
                      value: name,
                      label: name,
                      disabled: projectCosts.plantCosts.some(
                        (p) => p.id === name && p.id !== entry.id
                      )
                    }))
                  )}
                />
              ),
              unitOfMeasure: (
                <CostValue>{entry.unitOfMeasure || "-"}</CostValue>
              ),
              unitCost: <CostValue>£{formatAmount(entry.unitCost)}</CostValue>,
              quantity: (
                <InputNumber
                  aria-label="Quantity"
                  className="w-full"
                  value={entry.quantity}
                  precision={0}
                  min={0}
                  max={plantCategories.get(entry.name)?.qtyAvailableToHire}
                  onChange={(value) =>
                    updatePlantCost(entry.id, { quantity: value || 0 })
                  }
                />
              ),
              noOfDays: (
                <InputNumber
                  aria-label="No. of days"
                  className="w-full"
                  value={entry.noOfDays}
                  min={1}
                  onChange={(value) =>
                    updatePlantCost(entry.id, { noOfDays: value || 1 })
                  }
                />
              )
            }
          }))}
        />
      </CostSection>

      <CostSection
        title="Materials"
        icon={
          <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
            <BsBoxSeam className="text-[#22C55E] text-lg" />
          </div>
        }
        headerClassName="bg-green-50"
        itemCount={projectCosts.materialCosts.length > 0 ? 1 : 0}
        total={materialTotal}
        estimated={projectCosts.estimatedMaterialCost}
        expanded={expandedSections.materials}
        onToggle={() => toggleSection("materials")}
      >
        <label className="px-4 py-3 flex flex-col gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Material Costs
          </span>
          <InputNumber
            className="w-48"
            prefix="£"
            value={parseFloat(
              (
                projectCosts.materialCosts[0]?.amount ??
                projectCosts.estimatedMaterialCost
              ).toFixed(2)
            )}
            min={0}
            precision={2}
            onChange={(value) => updateMaterialAmount(value || 0)}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) =>
              (value ?? "").replace(/£\s?|(,*)/g, "") as unknown as number
            }
          />
        </label>
      </CostSection>

      <CostSection
        title="Subcontractors"
        icon={
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <TeamOutlined className="text-purple-600 text-base" />
          </div>
        }
        headerClassName="bg-purple-50"
        itemCount={projectCosts.subcontractorCosts.length}
        total={subcontractorTotal}
        estimated={projectCosts.estimatedSubcontractorCost}
        expanded={expandedSections.subcontractors}
        onToggle={() => toggleSection("subcontractors")}
        addButton={{
          label: "Add Subcontractor",
          onClick: addSubcontractorCost,
          loading: tradesLoading || subcontractorsLoading
        }}
      >
        <CostTable
          rowPadding="py-5"
          showAmount={false}
          columns={[
            {
              key: "subcontractor",
              header: "Trade / Subcontractor",
              className: "flex-1 min-w-[260px]"
            },
            { key: "amount", header: "Amount", className: "w-[200px]" }
          ]}
          rows={projectCosts.subcontractorCosts.map((entry, index) => ({
            key: index,
            removeLabel: "Remove subcontractor",
            onRemove: () => removeSubcontractorCost(index),
            cells: {
              subcontractor: (
                <CostOutline>
                  <div className="flex flex-col gap-3">
                    <Select
                      showSearch
                      aria-label="Trade"
                      className="w-full"
                      loading={tradesLoading}
                      placeholder="Select trade"
                      value={entry.tradeCode || undefined}
                      filterOption={filterOptionForLabelAndValueSearch}
                      onChange={(value) =>
                        updateSubcontractorCost(index, {
                          tradeCode: value,
                          name: ""
                        })
                      }
                      options={sortByLabel(
                        subcontractorTrades.map((trade) => ({
                          key: trade.code,
                          value: trade.code,
                          label: trade.description
                        }))
                      )}
                    />
                    <Select
                      showSearch
                      aria-label="Subcontractor"
                      className="w-full"
                      loading={subcontractorsLoading}
                      placeholder={
                        entry.tradeCode
                          ? "Select subcontractor"
                          : "Select trade first"
                      }
                      disabled={!entry.tradeCode}
                      value={entry.name || undefined}
                      filterOption={filterOptionForLabelAndValueSearch}
                      onChange={(value) =>
                        updateSubcontractorCost(index, { name: value })
                      }
                      options={sortByLabel(
                        (subcontractorsByTrade[entry.tradeCode] ?? []).map(
                          (subcontractor) => ({
                            key: subcontractor.no,
                            value: subcontractor.no,
                            label: subcontractor.name,
                            disabled: projectCosts.subcontractorCosts.some(
                              (s, i) =>
                                i !== index && s.name === subcontractor.no
                            )
                          })
                        )
                      )}
                    />
                  </div>
                </CostOutline>
              ),
              amount: (
                <InputNumber
                  aria-label="Amount"
                  className="w-full"
                  prefix="£"
                  precision={2}
                  value={entry.amount}
                  min={0}
                  onChange={(value) =>
                    updateSubcontractorCost(index, { amount: value || 0 })
                  }
                />
              )
            }
          }))}
        />
      </CostSection>
    </div>
  );
};
